import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as apiLogin, getMe } from '../api/auth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Impersonation-Token aus URL verarbeiten (von /admin/users "Login als")
    const urlParams = new URLSearchParams(window.location.search);
    const impersonateToken = urlParams.get('impersonate_token');
    if (impersonateToken) {
      sessionStorage.setItem('access_token', impersonateToken);
      sessionStorage.setItem('is_impersonating', 'true');
      // Token aus URL entfernen ohne Seiten-Reload
      window.history.replaceState({}, '', window.location.pathname);
    }

    const token = sessionStorage.getItem('access_token') || localStorage.getItem('access_token');
    if (!token) {
      setLoading(false);
      return;
    }
    getMe(token)
      .then(setUser)
      .catch(() => {
        localStorage.removeItem('access_token');
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('is_impersonating');
      })
      .finally(() => setLoading(false));
  }, []);

  // Cross-Tab-Sync: Wenn Admin-Portal in einem anderen Tab das Token wechselt,
  // wird die React-App automatisch neu synchronisiert.
  useEffect(() => {
    function handleStorageChange(e) {
      if (e.key !== 'access_token') return;
      const newToken = e.newValue;
      if (newToken) {
        getMe(newToken).then(setUser).catch(() => setUser(null));
      } else {
        setUser(null);
      }
    }
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  async function login(email, password) {
    const token = await apiLogin(email, password);
    localStorage.setItem('access_token', token);
    const me = await getMe(token);
    setUser(me);
    return me;
  }

  function logout() {
    const isImpersonating = sessionStorage.getItem('is_impersonating') === 'true';
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('is_impersonating');
    if (isImpersonating) {
      // Impersonation-Tab schließen, Original-Session bleibt erhalten
      window.close();
      return;
    }
    localStorage.removeItem('access_token');
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
